import { BUILD_ENV } from "../utils";
import { getDashboardUrl } from "../utils/report-link";
import { STATUS } from "../utils/status";

export { STATUS };

/* Die Adresse, die das Dashboard seinen Nutzern nennt (Support-Formular,
   Log-Collector). support@ und hi@ tauchen dort nicht auf. */
export const SUPPORT_EMAIL = "help@aivy.app";

/*
 * Wortlaut nach der Copy-Runde vom 2026-09-17 (drei Varianten, zwei Juroren).
 *
 * Aufbau jeder Meldung, in dieser Reihenfolge:
 *   1. Was passiert ist — in den Worten der Leserin, nicht des Systems.
 *   2. Warum — mit der echten Zahl (15 Minuten, pdfGenerator: Expires 60*15)
 *      und dem menschlichen Grund. Ohne Zahl wirkt die Regel willkuerlich,
 *      ohne Grund wie ein Bug; mit beidem versteht die Leserin, dass niemand
 *      einen Fehler gemacht hat.
 *   3. Genau eine Handlung, die von dieser Seite aus geht. Der Link laesst
 *      sich hier nicht erneuern (keine Anmeldung, keine Kennungen), also
 *      zurueck dorthin, wo der Report angefordert wurde. "Aivy-Dashboard" im
 *      Text, weil ATS-Nutzer unter "Dashboard" ihr eigenes Tool verstehen;
 *      "dort, wo du den Link bekommen hast" deckt ATS, E-Mail und
 *      weitergeleitete Links ab.
 *   4. Leise ein zweiter Weg (Support) und der Fehlercode.
 *
 * Register: du, wie im Dashboard (de.json: ~775 du-Formen gegen ~21 Sie) und
 * in PdfShell.js. Sentence case, keine Ausrufezeichen, kein
 * Entschuldigungstheater — Brandbook.
 *
 * nl: das Backend kennt es, die Seite hat aber weder Lade- noch
 * Kopfzeilentexte auf Niederlaendisch. Eine niederlaendische Karte unter einem
 * deutschen Spinner waere schlechter als eine durchgehend englische Seite —
 * deshalb faellt nl in utils/report-link.js auf en.
 */
const TEXTS = {
  de: {
    brand: "Aivy",
    codeLabel: "Fehlercode",
    link_expired: {
      documentTitle: "Link abgelaufen | Aivy®",
      title: "Dieser Link ist abgelaufen",
      body:
        // ‑ = geschuetzter Bindestrich: "Aivy-Dashboard" soll nicht am
        // Zeilenende in "Aivy-" / "Dashboard" zerfallen.
        "Report-Links gelten 15 Minuten, damit persönliche Ergebnisse geschützt bleiben. Öffne den Report erneut – im Aivy‑Dashboard oder dort, wo du den Link bekommen hast.",
      primary: "Zum Dashboard",
      secondary: "Support kontaktieren",
      mailSubject: "Report-Link abgelaufen (link_expired)",
    },
    app_failed: {
      documentTitle: "Report nicht geladen | Aivy®",
      title: "Der Report lässt sich gerade nicht laden",
      body:
        "Das kann an der Verbindung liegen oder an uns. Lade die Seite neu – bleibt der Fehler, melde dich beim Support, wir schauen uns das an.",
      primary: "Seite neu laden",
      secondary: "Support kontaktieren",
      mailSubject: "Report lädt nicht (app_failed)",
    },
  },
  en: {
    brand: "Aivy",
    codeLabel: "Error code",
    link_expired: {
      documentTitle: "Link expired | Aivy®",
      title: "This link has expired",
      body:
        "Report links are valid for 15 minutes so personal results stay protected. Open the report again from the Aivy dashboard or wherever you got the link.",
      primary: "Open dashboard",
      secondary: "Contact support",
      mailSubject: "Report link expired (link_expired)",
    },
    app_failed: {
      documentTitle: "Report not loaded | Aivy®",
      title: "The report can't load right now",
      body:
        "This may be a connection hiccup or something on our side. Reload the page – if it keeps failing, contact support and we'll look into it.",
      primary: "Reload page",
      secondary: "Contact support",
      mailSubject: "Report not loading (app_failed)",
    },
  },
};

/* Nur der Betreff, kein Body: weder die URL noch der S3-Key duerfen in einer
   Mail landen — genau die Daten schuetzt die kurze Gueltigkeit. */
const supportMailto = (subject) =>
  `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;

/**
 * @param {string} reason - STATUS-Wert; Unbekanntes faellt auf app_failed,
 *   statt im Render zu werfen (die ErrorBoundary darueber zeigt genau diese
 *   Seite — ein Wurf hier waere eine Fehlerseite, die an ihrer eigenen
 *   Fehlerseite scheitert)
 * @param {string} [language] - "de" | "en", alles andere faellt auf de
 */
export const getStatusTexts = (reason, language) => {
  /* Aufgeloest zurueckgegeben, damit die Seite <html lang> auf die Sprache
     setzt, die wirklich auf der Karte steht — sonst laese ein Screenreader
     deutschen Text mit franzoesischer Stimme. */
  const languageKey = TEXTS[language] ? language : "de";
  const languageTexts = TEXTS[languageKey];

  /* Gegen die bekannten Zustaende pruefen und nicht gegen "gibt es den Key":
     ein Grund wie "brand" traefe sonst den Marken-String, und der hat keine
     Ueberschrift — die Fehlerseite wuerde an ihrer eigenen Anzeige scheitern. */
  const code = Object.values(STATUS).includes(reason)
    ? reason
    : STATUS.APP_FAILED;
  const entry = languageTexts[code];

  const primary =
    code === STATUS.LINK_EXPIRED
      ? { label: entry.primary, href: getDashboardUrl(BUILD_ENV) }
      : { label: entry.primary, reload: true };

  return {
    code,
    language: languageKey,
    brand: languageTexts.brand,
    codeLabel: languageTexts.codeLabel,
    documentTitle: entry.documentTitle,
    title: entry.title,
    body: entry.body,
    primary,
    secondary: {
      label: entry.secondary,
      href: supportMailto(entry.mailSubject),
    },
  };
};
